/**
 * useFormDraft — keep what the user typed when a form is closed part-way.
 *
 * A form wired to this hook writes its current values to localStorage as the
 * user types, and offers them back the next time the same form is opened. Close
 * the dialog by accident, navigate away, reload the tab, come back tomorrow —
 * the half-finished entry is still there to carry on from or to overwrite.
 *
 * WHAT CLEARS A DRAFT
 *   - a successful submit (the caller calls `clear()`)
 *   - the user pressing Discard (`discard()`)
 *   - the draft ageing past MAX_AGE_MS
 * Closing or cancelling the form deliberately does NOT clear it — that is the
 * whole point of the feature.
 *
 * USAGE — the form keeps its own state exactly as it already does; this only
 * observes it and hands values back on reopen:
 *
 *   const draft = useFormDraft({
 *     formId: 'ds-wms:create-work',
 *     enabled: isOpen,
 *     values: { wrk_nm, wrk_type_id, lctn_tx },   // whatever should survive
 *     onRestore: (v) => {
 *       if (v.wrk_nm != null) setWrkNm(v.wrk_nm);
 *       if (v.wrk_type_id != null) setWrkTypeId(v.wrk_type_id);
 *       if (v.lctn_tx != null) setLctnTx(v.lctn_tx);
 *     },
 *   });
 *
 *   // on a successful save:
 *   draft.clear();
 *
 *   // optional banner:
 *   {draft.restored && <>Draft restored <button onClick={draft.discard}>Discard</button></>}
 *
 * EDIT FORMS: put the record id in `formId` (`'…:edit-work:' + wrkId`) so each
 * record keeps its own draft and one record's edits can never appear under
 * another's.
 *
 * DO NOT PASS: passwords, OTPs, or anything else that should not sit in
 * localStorage — just leave those out of `values`. File / FileList objects are
 * dropped automatically because they cannot be serialised.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/** Bump when the stored shape changes so old entries are ignored, not misread. */
const STORAGE_VERSION = 'v1';
const KEY_PREFIX = `form-draft:${STORAGE_VERSION}:`;

/** Drafts older than this are treated as gone. */
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/** How long the user must pause before a write — typing shouldn't hit storage per keystroke. */
const WRITE_DEBOUNCE_MS = 500;

export interface FormDraftOptions<T extends Record<string, any>> {
  /** Stable id for this form. Include a record id for edit forms. */
  formId: string;
  /** Track only while the form is actually open. */
  enabled?: boolean;
  /** The values to preserve. Pass a plain object; it may be rebuilt each render. */
  values: T;
  /** Called once per open with a saved draft, so the form can restore its state. */
  onRestore?: (values: Partial<T>) => void;
}

export interface FormDraft {
  /** True when this open restored a saved draft. */
  restored: boolean;
  /** When the restored draft was last written, or null. */
  savedAt: Date | null;
  /** Forget the draft — call after a successful submit. */
  clear: () => void;
  /** Forget the draft AND drop the restored flag — the Discard button. */
  discard: () => void;
}

/**
 * A user's drafts must not be offered to whoever logs in next on a shared
 * machine, so the key is scoped to the signed-in user. The id is read from the
 * JWT already in localStorage; if it cannot be read the drafts are scoped to
 * 'anon', which is still per-browser-profile.
 */
function currentUserScope(): string {
  try {
    const raw =
      localStorage.getItem('x-access-token') ||
      localStorage.getItem('auth_token') ||
      localStorage.getItem('app-auth-token') ||
      '';
    const part = raw.replace(/^Bearer\s+/i, '').split('.')[1];
    if (!part) return 'anon';
    const payload = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
    const id = payload?.id ?? payload?.userId ?? payload?.usr_id ?? payload?.sub;
    return id != null ? String(id) : 'anon';
  } catch {
    return 'anon';
  }
}

/** Values worth keeping? A form the user only opened and closed leaves nothing. */
function hasContent(values: Record<string, any>): boolean {
  return Object.values(values).some((v) => {
    if (v == null) return false;
    if (typeof v === 'string') return v.trim() !== '';
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'number') return !Number.isNaN(v) && v !== 0;
    if (typeof v === 'boolean') return v;
    if (typeof v === 'object') return Object.keys(v).length > 0;
    return true;
  });
}

/**
 * JSON with the un-storable parts removed rather than the whole write failing.
 * A File in the values would otherwise throw and lose the entire draft.
 */
function serialise(values: Record<string, any>): string | null {
  try {
    return JSON.stringify(values, (_k, v) => {
      if (typeof v === 'function') return undefined;
      if (typeof File !== 'undefined' && v instanceof File) return undefined;
      if (typeof FileList !== 'undefined' && v instanceof FileList) return undefined;
      if (typeof Blob !== 'undefined' && v instanceof Blob) return undefined;
      return v;
    });
  } catch {
    return null;
  }
}

export function useFormDraft<T extends Record<string, any>>({
  formId,
  enabled = true,
  values,
  onRestore,
}: FormDraftOptions<T>): FormDraft {
  // Decoding the JWT is cheap but not free, and this hook sits in forms that
  // re-render on every keystroke — so resolve the key once per formId.
  const storageKey = useMemo(
    () => `${KEY_PREFIX}${currentUserScope()}:${formId}`,
    [formId],
  );

  const [restored, setRestored] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  // Callers pass an inline arrow; keep it in a ref so the restore effect depends
  // only on whether the form is open, not on a function identity that changes
  // every render (which would re-restore and stamp over live edits).
  const onRestoreRef = useRef(onRestore);
  onRestoreRef.current = onRestore;

  // Writing is blocked until the restore attempt for this open has finished.
  // Without this the form's blank initial state races the read and overwrites
  // the very draft we are about to offer back.
  const readyToWrite = useRef(false);
  const writeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const remove = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
    } catch {
      /* storage unavailable — nothing to remove */
    }
    setSavedAt(null);
  }, [storageKey]);

  const clear = useCallback(() => {
    if (writeTimer.current) clearTimeout(writeTimer.current);
    // Block the pending debounced write from re-creating what we just deleted.
    readyToWrite.current = false;
    remove();
  }, [remove]);

  const discard = useCallback(() => {
    clear();
    setRestored(false);
  }, [clear]);

  // ── Restore, once per open ────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) {
      // Closing resets the gate so the next open restores again rather than
      // immediately writing this form's post-close cleared state over the draft.
      readyToWrite.current = false;
      setRestored(false);
      if (writeTimer.current) clearTimeout(writeTimer.current);
      return;
    }

    let saved: { at: number; values: Partial<T> } | null = null;
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) saved = JSON.parse(raw);
    } catch {
      saved = null;
    }

    const fresh = saved && typeof saved.at === 'number' && Date.now() - saved.at < MAX_AGE_MS;
    if (saved && !fresh) remove();

    if (fresh && saved!.values && hasContent(saved!.values)) {
      // Deferred a tick: forms typically clear their fields in their own
      // open/close effect, and that must land before values are put back.
      const t = setTimeout(() => {
        onRestoreRef.current?.(saved!.values);
        setSavedAt(new Date(saved!.at));
        setRestored(true);
        readyToWrite.current = true;
      }, 0);
      return () => clearTimeout(t);
    }

    readyToWrite.current = true;
    return undefined;
  }, [enabled, storageKey, remove]);

  // ── Save, debounced, whenever the tracked values change ────────────────────
  const serialised = serialise(values);
  useEffect(() => {
    if (!enabled || !readyToWrite.current || serialised == null) return;

    if (writeTimer.current) clearTimeout(writeTimer.current);
    writeTimer.current = setTimeout(() => {
      let parsed: Record<string, any>;
      try {
        parsed = JSON.parse(serialised);
      } catch {
        return;
      }
      // An emptied form is not a draft — drop it rather than storing a blank.
      if (!hasContent(parsed)) {
        remove();
        return;
      }
      try {
        localStorage.setItem(storageKey, JSON.stringify({ at: Date.now(), values: parsed }));
        setSavedAt(new Date());
      } catch {
        /* quota exceeded / storage disabled — a lost draft must never break the form */
      }
    }, WRITE_DEBOUNCE_MS);

    return () => {
      if (writeTimer.current) clearTimeout(writeTimer.current);
    };
  }, [serialised, enabled, storageKey, remove]);

  // Flush on unload so a draft survives a tab closed mid-sentence, inside the
  // debounce window.
  useEffect(() => {
    if (!enabled) return undefined;
    const flush = () => {
      if (!readyToWrite.current || serialised == null) return;
      try {
        const parsed = JSON.parse(serialised);
        if (!hasContent(parsed)) return;
        localStorage.setItem(storageKey, JSON.stringify({ at: Date.now(), values: parsed }));
      } catch {
        /* nothing we can do at unload time */
      }
    };
    window.addEventListener('pagehide', flush);
    return () => window.removeEventListener('pagehide', flush);
  }, [serialised, enabled, storageKey]);

  return { restored, savedAt, clear, discard };
}

export default useFormDraft;
